import { useState, useEffect, useCallback } from 'react';
import { postPushSubscription } from 'utils/network/http';
import {
  isPushNotificationSupported,
  askUserPermission,
  registerServiceWorker,
  createNotificationSubscription,
  getUserSubscription,
} from 'utils/network/pushNotifications';
import { sendEventToSegment } from 'utils/functions/Tracking';
import TRACKING_EVENTS from 'utils/constants/trackingEvents';
import { loadVersionBrowser } from 'utils/functions';

export const usePushNotifications = () => {
  const pushNotificationSupported = isPushNotificationSupported();
  const [userConsent, setSuserConsent] = useState(
    window.Notification?.permission
  );
  const [userSubscription, setUserSubscription] = useState(null);
  const [pushServerSubscriptionId, setPushServerSubscriptionId] = useState();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (pushNotificationSupported) {
      setLoading(true);
      setError(false);
      registerServiceWorker().then(() => {
        setLoading(false);
      });
    }
  }, [pushNotificationSupported]);

  useEffect(() => {
    setLoading(true);
    setError(false);
    const getExistingSubscription = async () => {
      try {
        const existingSubscription = await getUserSubscription();
        setUserSubscription(existingSubscription);
        setLoading(false);
      } catch (e) {
        console.error('GetUserSubscription error', e);
      }
    };
    getExistingSubscription();
  }, []);

  const sendSubscriptionToPushServer = useCallback(async (userSub) => {
    setLoading(true);
    setError(false);
    if (userSub) {
      const endpointParts = userSub.endpoint.split('/');
      const registration_id = endpointParts[endpointParts.length - 1];
      const browser = loadVersionBrowser();
      var data = {
        browser: browser.name.toUpperCase(),
        p256dh: btoa(
          String.fromCharCode.apply(
            null,
            new Uint8Array(userSub.getKey('p256dh'))
          )
        ),
        auth: btoa(
          String.fromCharCode.apply(
            null,
            new Uint8Array(userSub.getKey('auth'))
          )
        ),
        name: 'FleetApp',
        registration_id: registration_id,
      };
      try {
        const response = await postPushSubscription(data);
        setPushServerSubscriptionId(response.id);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError(err);
      }
    }
  }, []);

  const activatePushNotifications = useCallback(async () => {
    sendEventToSegment(TRACKING_EVENTS.ASK_PUSH);
    const consent = await askUserPermission();
    setSuserConsent(consent);
    if (consent !== 'granted') {
      sendEventToSegment(TRACKING_EVENTS.BLOCK_PUSH);
      console.log('User does not accept pushNotifications');
      return;
    } else {
      sendEventToSegment(TRACKING_EVENTS.ALLOW_PUSH);
    }

    if (!userSubscription) {
      try {
        const newUserSubscription = await createNotificationSubscription();
        setUserSubscription(newUserSubscription);
        console.log('Send user', newUserSubscription);
        sendSubscriptionToPushServer(newUserSubscription);
      } catch (e) {
        console.error('Error creating subscription', e);
      }
      return;
    }
    sendSubscriptionToPushServer(userSubscription);
  }, [userSubscription, sendSubscriptionToPushServer]);

  /**
   * returns all the stuff needed by a Component
   */
  return {
    pushServerSubscriptionId,
    userConsent,
    pushNotificationSupported,
    userSubscription,
    error,
    loading,
    activatePushNotifications,
  };
};
